/**
 * Storage side of saving an artwork crop (JARVIS-391): reads the original's
 * bytes for the editor and writes the crop pair back beside it.
 *
 * Bytes come through `Firestore.getBlob` rather than a plain `fetch` of the
 * download URL, so the read uses the same auth and CORS path as every other
 * Storage call and the decoded bitmap can be uploaded to a WebGL texture
 * without tainting the canvas.
 *
 * Pure path and field logic lives in `artworkCropHelpers.ts`.
 */
import Firestore from "#classes/Firestore"
import {
  deriveCropPaths,
  type SavedCropFields,
} from "#utils/artworkCropHelpers"
import { WARP_CONTENT_TYPE } from "#utils/imageWarp"

import type { UploadMetadata } from "firebase/storage"

/** Matches the thumbnail quality the mobile editor exports at. */
export const CROP_THUMB_JPEG_QUALITY = 0.8

export interface CropBlobs {
  crop: Blob
  thumb: Blob
}

/**
 * Decodes the original for editing. `imageOrientation: "from-image"` bakes
 * EXIF orientation into the pixels exactly as the rendition pipeline does,
 * so the corners the user drags sit on the image they can actually see.
 * `artwork.rotation` is NOT applied here — it stays a display-time value and
 * the editor applies it when painting the stage.
 */
export const loadArtworkBitmap = async (
  objectPath: string
): Promise<ImageBitmap> => {
  const blob = await Firestore.getInstance().getBlob(objectPath)
  return createImageBitmap(blob, { imageOrientation: "from-image" })
}

const cropMetadata = (variant: "crop" | "thumb"): UploadMetadata => ({
  contentType: WARP_CONTENT_TYPE,
  // Immutable is safe because the names are timestamped: a re-crop writes a
  // new object rather than replacing this one.
  cacheControl: "public, max-age=31536000, immutable",
  customMetadata: { cropVariant: variant },
})

/**
 * Writes the crop and its thumbnail next to `originalPath` and resolves to
 * the four fields the artwork entry records.
 *
 * Both uploads run together: a half-written pair is already handled, because
 * nothing is recorded on the doc until this resolves, so a failure leaves
 * only an orphaned object and the piece still shows its original.
 */
export const uploadArtworkCrop = async (
  blobs: CropBlobs,
  originalPath: string,
  timestamp: number = Date.now()
): Promise<SavedCropFields> => {
  const { cropPath, thumbPath } = deriveCropPaths(originalPath, timestamp)
  const storage = Firestore.getInstance()
  const [cropUrl, thumbnailUrl] = await Promise.all([
    storage.uploadBlob(blobs.crop, cropPath, cropMetadata("crop")),
    storage.uploadBlob(blobs.thumb, thumbPath, cropMetadata("thumb")),
  ])
  return { cropUrl, cropPath, thumbnailUrl, thumbnailPath: thumbPath }
}
